import { useState, useEffect, useMemo } from 'react';
import { Direction, TrafficDataPoint } from '../types';
import { logger } from '../lib/logger';
import { trackEvent } from '../lib/posthog';
import {
  tripsApi,
  trafficApi,
  directionToApi,
  CreateTripRequest,
  TrafficHourPoint,
  PredictionMeta,
  EtaResponse,
} from '../lib/api';
import { departureTimeToTime } from '../lib/utils';

interface SaveTripParams {
  userId: string | null;
  tripId?: string;
  routeId: string;
  tripDate: string;
  departureTime: number;
  direction: Direction;
  smartAlerts: boolean;
  estimatedDurationMinutes?: number;
  estimatedDistanceKm?: number;
  trafficLevel?: string;
}

interface UseTripProps {
  tripDate: string;
  direction: Direction;
  selectedRouteId: string;
  departureTime: number | null;
  enabled?: boolean;
}

interface EtaState {
  data: EtaResponse | null;
  loading: boolean;
  error: string | null;
}

interface TrafficDataState {
  data: TrafficDataPoint[];
  predictionMeta: PredictionMeta | null;
  loading: boolean;
  error: string | null;
}

/**
 * Hook para manejar viajes - usa API FastAPI
 */
export const useTrip = ({ tripDate, direction, selectedRouteId, departureTime, enabled = true }: UseTripProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [trafficRefetchKey, setTrafficRefetchKey] = useState(0);
  const [etaState, setEtaState] = useState<EtaState>({
    data: null,
    loading: false,
    error: null,
  });
  const [trafficData, setTrafficData] = useState<TrafficDataState>({
    data: [],
    predictionMeta: null,
    loading: false,
    error: null,
  });

  const directionString = directionToApi(direction);

  const refetchTrafficData = () => setTrafficRefetchKey(k => k + 1);

  // Fetch traffic distribution from API (48 points, 30-min intervals)
  useEffect(() => {
    if (!enabled || !selectedRouteId || !tripDate) return;

    const fetchTrafficData = async () => {
      setTrafficData(prev => ({ ...prev, loading: true, error: null }));

      try {
        const response = await trafficApi.getDistribution(
          selectedRouteId,
          directionString,
          tripDate
        );

        // Transform API response to TrafficDataPoint format expected by components
        const trafficPoints: TrafficDataPoint[] = response.distribution.map((item: TrafficHourPoint) => {
          const normalized = normalizeLegacyTrafficPoint({
            item,
            routeId: selectedRouteId,
            direction: directionString,
            tripDate,
            dateType: response.prediction_meta?.date_type ?? null,
          });
          const displayLevel = item.displayLevel ?? deriveDisplayLevel(
            normalized.estimatedMinutes,
            selectedRouteId
          );
          const apiDensity = Math.round(item.level * 70); // Compatibilidad con datos API crudos
          const registeredCount = item.registeredCount || 0;
          const registeredBoost = 0;
          const finalDensity = durationToDensity(normalized.estimatedMinutes, selectedRouteId);
          return {
            hour: item.hour,
            halfHour: item.halfHour,
            density: finalDensity,
            apiDensity,
            registeredBoost,
            finalDensity,
            registeredDensity: finalDensity,
            registeredCount,
            label: item.halfHour === 0
              ? `${item.hour.toString().padStart(2, '0')}:00`
              : '',
            durationMinutes: normalized.estimatedMinutes,
            durationOptimisticMinutes: normalized.optimisticMinutes,
            durationPessimisticMinutes: normalized.pessimisticMinutes,
            displayLevel,
            explanation: response.prediction_meta?.context_message ?? null,
            trafficLevel: displayLevelToTrafficLevel(displayLevel),
          };
        });

        setTrafficData({
          data: trafficPoints,
          predictionMeta: response.prediction_meta ?? null,
          loading: false,
          error: null,
        });
      } catch (error) {
        logger.error('Error fetching traffic data', error instanceof Error ? error : new Error(String(error)), {
          selectedRouteId,
          direction: directionString,
          tripDate,
        });
        setTrafficData({
          data: [],
          predictionMeta: null,
          loading: false,
          error: 'Error al obtener datos de tráfico',
        });
      }
    };

    // Refetch forzado (después de guardar viaje) sin delay; el debounce solo aplica al cambio de parámetros
    const delay = trafficRefetchKey > 0 ? 0 : 300;
    const timeoutId = setTimeout(fetchTrafficData, delay);
    return () => clearTimeout(timeoutId);
  }, [enabled, selectedRouteId, directionString, tripDate, trafficRefetchKey]);

  // Refresco automático cada 30 min mientras la pestaña esté visible
  useEffect(() => {
    if (!enabled) return;
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        refetchTrafficData();
      }
    }, 30 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [enabled]);

  // Fetch ETA when departure time is selected
  useEffect(() => {
    if (!enabled || !selectedRouteId || !tripDate || departureTime === undefined || departureTime === null) {
      setEtaState({ data: null, loading: false, error: null });
      return;
    }

    const hour = Math.floor(departureTime);
    const minute = (departureTime % 1) >= 0.5 ? 30 : 0;
    const departureTimeIso = `${tripDate}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;

    const fetchEta = async () => {
      setEtaState(prev => ({ ...prev, loading: true, error: null }));
      try {
        const result = await trafficApi.getEta(selectedRouteId, directionString, departureTimeIso);
        setEtaState({ data: result, loading: false, error: null });
      } catch (error) {
        logger.error('Error fetching ETA', error instanceof Error ? error : new Error(String(error)), {
          selectedRouteId,
          direction: directionString,
          departureTimeIso,
        });
        setEtaState({ data: null, loading: false, error: 'Error al calcular tiempo estimado' });
      }
    };

    const timeoutId = setTimeout(fetchEta, 300);
    return () => clearTimeout(timeoutId);
  }, [enabled, selectedRouteId, directionString, departureTime, tripDate]);

  /**
   * Save trip via API
   */
  const saveTrip = async (params: SaveTripParams): Promise<{ success: boolean; error?: string }> => {
    setIsSaving(true);
    try {
      const scheduledTime = departureTimeToTime(params.departureTime);

      const request: CreateTripRequest = {
        routeId: params.routeId,
        direction: directionToApi(params.direction),
        travelDate: params.tripDate,
        scheduledTime,
        notificationsEnabled: params.smartAlerts,
      };

      const result = params.tripId
        ? await tripsApi.update(params.tripId, request)
        : await tripsApi.create(request);

      logger.info('Trip saved successfully', {
        tripId: result.id,
        routeId: params.routeId,
        tripDate: params.tripDate,
        mode: params.tripId ? 'update' : 'create',
      });

      trackEvent(params.tripId ? 'trip_updated' : 'trip_saved', {
        routeId: params.routeId,
        direction: directionToApi(params.direction),
        tripDate: params.tripDate,
        isAnonymous: params.userId === null,
        trafficLevel: result.trafficLevel,
        platform: 'web',
      });

      return { success: true };
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      logger.error('Error saving trip', error, {
        userId: params.userId,
        routeId: params.routeId,
      });

      if (err instanceof TypeError) {
        return { success: false, error: 'Error de conexión. Verifica tu internet.' };
      }
      return { success: false, error: error.message || 'Error inesperado. Contacta soporte.' };
    } finally {
      setIsSaving(false);
    }
  };

  // Fallback: duración desde datos del gráfico cuando /eta no está disponible
  const fallbackDurationMin = useMemo(() => {
    if (departureTime === null || trafficData.data.length === 0) return null;
    const hour = Math.floor(departureTime);
    const half = (departureTime % 1) >= 0.5 ? 30 : 0;
    const point = trafficData.data.find(d => d.hour === hour && d.halfHour === half);
    return point?.durationMinutes ?? null;
  }, [departureTime, trafficData.data]);

  return {
    isSaving,
    saveTrip,
    refetchTrafficData,
    trafficData: trafficData.data,
    predictionMeta: trafficData.predictionMeta,
    loadingTrafficData: trafficData.loading,
    etaData: etaState.data,
    loadingEta: etaState.loading,
    fallbackDurationMin,
  };
};

function deriveDisplayLevel(
  estimatedMinutes: number,
  routeId: string
): 'low' | 'medium' | 'high' | 'critical' {
  const baseline = routeId === '68' ? 100 : 90;
  const extraMinutes = estimatedMinutes - baseline;
  if (extraMinutes < 15) return 'low';
  if (extraMinutes < 35) return 'medium';
  if (extraMinutes < 75) return 'high';
  return 'critical';
}

function durationToDensity(
  estimatedMinutes: number,
  routeId: string
): number {
  const baseline = routeId === '68' ? 100 : 90;
  const extra = Math.max(0, estimatedMinutes - baseline);
  const density = 25 + Math.sqrt(extra) * 6;
  return Math.round(Math.min(88, Math.max(20, density)));
}

function normalizeLegacyTrafficPoint({
  item,
  routeId,
  direction,
  tripDate,
  dateType,
}: {
  item: TrafficHourPoint;
  routeId: string;
  direction: string;
  tripDate: string;
  dateType: string | null;
}): {
  estimatedMinutes: number;
  optimisticMinutes: number | null;
  pessimisticMinutes: number | null;
} {
  if (item.displayLevel) {
    return {
      estimatedMinutes: item.estimatedMinutes,
      optimisticMinutes: item.estimatedMinutesOptimistic ?? null,
      pessimisticMinutes: item.estimatedMinutesPessimistic ?? null,
    };
  }

  const seasonal = estimateSeasonalDuration(routeId, direction, tripDate, item.hour);
  const legacyEstimate = Math.min(item.estimatedMinutes, seasonal.expectedMinutes);

  if (dateType && !['normal', 'holiday', 'summer'].includes(dateType)) {
    return {
      estimatedMinutes: item.estimatedMinutes,
      optimisticMinutes: item.estimatedMinutesOptimistic ?? null,
      pessimisticMinutes: item.estimatedMinutesPessimistic ?? null,
    };
  }

  return {
    estimatedMinutes: legacyEstimate,
    optimisticMinutes: Math.min(item.estimatedMinutesOptimistic ?? seasonal.optimisticMinutes, seasonal.optimisticMinutes),
    pessimisticMinutes: Math.min(item.estimatedMinutesPessimistic ?? seasonal.pessimisticMinutes, seasonal.pessimisticMinutes),
  };
}

function estimateSeasonalDuration(
  routeId: string,
  direction: string,
  tripDate: string,
  hour: number
): {
  expectedMinutes: number;
  optimisticMinutes: number;
  pessimisticMinutes: number;
} {
  const baseline = routeId === '68' ? 100 : 90;
  const weekday = new Date(`${tripDate}T00:00:00`).getDay();
  const [windowMin, windowMax] = routeWindowAdjustment(direction, weekday, hour);
  const [dayMin, dayMax] = dayAdjustment(direction, weekday);
  const optimisticMinutes = Math.round(Math.max(45, baseline + windowMin + dayMin));
  const pessimisticMinutes = Math.round(Math.max(optimisticMinutes + 5, baseline + windowMax + dayMax));
  const expectedMinutes = Math.round((optimisticMinutes + pessimisticMinutes) / 2);

  return {
    expectedMinutes,
    optimisticMinutes,
    pessimisticMinutes,
  };
}

function routeWindowAdjustment(direction: string, weekday: number, hour: number): [number, number] {
  if (direction === 'santiago-litoral') {
    if (weekday === 5) {
      if (hour >= 15 && hour < 17) return [5, 25];
      if (hour >= 17 && hour < 20) return [25, 65];
      if (hour >= 20 && hour < 21) return [15, 45];
      if (hour >= 21) return [0, 20];
    }
    if (hour >= 10 && hour < 15) return [-5, 10];
    if (hour >= 15 && hour < 17) return [0, 15];
    if (hour >= 17 && hour < 20) return [5, 25];
    if (hour >= 20 && hour < 21) return [0, 15];
    if (hour >= 21) return [-5, 10];
    return [0, 10];
  }

  if (weekday === 0) {
    if (hour >= 14 && hour < 17) return [-20, 20];
    if (hour >= 17 && hour < 21) return [5, 45];
    if (hour >= 21 && hour < 23) return [-5, 20];
  }
  if (weekday === 1 && hour >= 15 && hour < 21) return [0, 20];
  if (hour >= 15 && hour < 21) return [-5, 15];
  return [-5, 10];
}

function dayAdjustment(direction: string, weekday: number): [number, number] {
  if (direction === 'litoral-santiago' && weekday === 0) return [0, 5];
  if (weekday === 5) return [0, 5];
  if (weekday === 2 || weekday === 3) return [0, 5];
  return [0, 5];
}

function displayLevelToTrafficLevel(
  displayLevel: 'low' | 'medium' | 'high' | 'critical'
): 'LIGHT' | 'MODERATE' | 'HEAVY' | 'SEVERE' {
  if (displayLevel === 'low') return 'LIGHT';
  if (displayLevel === 'medium') return 'MODERATE';
  if (displayLevel === 'high') return 'HEAVY';
  return 'SEVERE';
}
